import { useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { PolicyBundle } from '@/shared/api/generated/policy';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';

import { useDeleteBundle, versionsQuery } from '../api/policy';
import { deletabilityOf } from '../model/bundles';

/* Bundles under any name but the governing one. **Stored, and consulted by nothing.**
 *
 * Only the governing bundle decides access, so these are kept out of the way — collapsed, and said
 * to decide nothing — rather than listed beside it as if they were alternatives. What a reader can
 * do with one is delete it, which is refused while one of its versions is active: deleting a bundle
 * is housekeeping, and it must not change authorization as a side effect.
 */

export interface OtherBundlesProps {
  readonly bundles: readonly PolicyBundle[];
  readonly canDelete: boolean;
  readonly onFlash: (message: string) => void;
}

export function OtherBundles({
  bundles,
  canDelete,
  onFlash,
}: OtherBundlesProps): ReactElement | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<PolicyBundle | null>(null);
  const remove = useDeleteBundle();
  const versions = useQueries({
    queries: bundles.map((bundle) => ({ ...versionsQuery(bundle.id), enabled: open })),
  });

  if (bundles.length === 0) return null;

  const counted =
    deleting === null ? undefined : versions[bundles.indexOf(deleting)]?.data;

  return (
    <section className="lq-pq__others" aria-labelledby="pq-others">
      <button
        id="pq-others"
        type="button"
        className="lq-pq__othersToggle"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
        }}
      >
        <span className="lq-pq__chev" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        {t('policy.others.title', { count: bundles.length })}
        <span className="lq-pq__othersNote">{t('policy.others.note')}</span>
      </button>
      {open ? (
        <ul className="lq-pq__othersList">
          {bundles.map((bundle, index) => {
            const read = versions[index];
            const deletability = deletabilityOf(read?.data);
            const blocked = !canDelete || deletability !== 'deletable';
            const reason = !canDelete
              ? t('policy.others.noPermission')
              : deletability === 'blocked-active'
                ? t('policy.others.blockedActive')
                : deletability === 'unknown'
                  ? t('policy.others.checking')
                  : undefined;
            return (
              <li key={bundle.id} className="lq-pq__otherRow">
                <span className="lq-pq__otherName">{bundle.name}</span>
                <span className="lq-pq__otherDesc">
                  {bundle.description || t('policy.others.noDescription')}
                  {read?.data === undefined
                    ? ''
                    : ` · ${t('policy.others.versions', { count: read.data.length })}`}
                </span>
                <button
                  type="button"
                  className="lq-pq__btn lq-pq__btn--alarm"
                  disabled={blocked}
                  title={reason}
                  aria-label={t('policy.others.delete', { name: bundle.name })}
                  onClick={() => {
                    setDeleting(bundle);
                  }}
                >
                  {t('policy.others.deleteShort')}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title={t('policy.others.confirmTitle', { name: deleting?.name ?? '' })}
        message={t('policy.others.confirmBody', { count: counted?.length ?? 0 })}
        confirmLabel={t('policy.others.confirmDo')}
        cancelLabel={t('common.cancel')}
        tone="danger"
        busy={remove.isPending}
        error={remove.isError ? remove.error.message : undefined}
        onConfirm={() => {
          if (deleting === null) return;
          const name = deleting.name;
          remove.mutate(deleting.id, {
            onSuccess: () => {
              setDeleting(null);
              onFlash(t('policy.others.deleted', { name }));
            },
          });
        }}
        onCancel={() => {
          setDeleting(null);
          remove.reset();
        }}
      />
    </section>
  );
}
